import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { clearSocketBuffer, connectSocket, socket } from '../socket';
import {
  buildGomokuLobbyRoute
} from '../games/routes';

const GOMOKU_BOARD_COLS = 15;
const GOMOKU_BOARD_ROWS = 20;

function normalizeRoomCode(roomCode) {
  return String(roomCode || '').trim().toUpperCase();
}

function roomCodeFromPayload(payload = {}) {
  return normalizeRoomCode(payload?.roomCode || payload?.code || payload?.room?.code);
}

export function createPlaceholderBoard(rows = GOMOKU_BOARD_ROWS, cols = GOMOKU_BOARD_COLS) {
  return Array.from({ length: rows }, () => Array(cols).fill(null));
}

export function firstOpenGomokuSeatIndex(players = []) {
  const occupiedSeats = new Set(players.map((player) => Number(player.seatIndex)).filter(Number.isInteger));
  if (!occupiedSeats.has(0)) {
    return 0;
  }
  if (!occupiedSeats.has(1)) {
    return 1;
  }
  return -1;
}

export function gomokuStoneForUser(state, userId) {
  if (!state || !userId) {
    return '';
  }
  if (state.blackUserId === userId) {
    return 'B';
  }
  if (state.whiteUserId === userId) {
    return 'W';
  }
  return '';
}

export function canPlayGomokuMove({ state, userId, row, col }) {
  return Boolean(
    state
    && state.status === 'playing'
    && state.currentTurnUserId === userId
    && Number.isInteger(row)
    && Number.isInteger(col)
    && Array.isArray(state.board?.[row])
    && state.board[row][col] === null
  );
}

function participantName(room, userId) {
  const player = room?.players?.find((entry) => entry.userId === userId);
  return player?.displayName || '';
}

export function gomokuStatusCopy({ room, state, userId }) {
  if (!state) {
    return '等待另一位玩家';
  }
  if (state.status === 'finished' && state.winnerUserId) {
    if (state.winnerUserId === userId) {
      return '你赢了，这局已经结束。';
    }
    const winnerName = participantName(room, state.winnerUserId);
    return winnerName ? `对局结束，${winnerName} 获胜。` : '对局已经结束。';
  }
  if (state.currentTurnUserId === userId) {
    return '轮到你落子。';
  }
  const nextPlayerName = participantName(room, state.currentTurnUserId);
  return nextPlayerName ? `等待 ${nextPlayerName} 落子。` : '等待对手落子。';
}

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const GomokuRoomPage = () => {
  const { roomCode: routeRoomCode = '' } = useParams();
  const { state: navigationState } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const normalizedRoomCode = normalizeRoomCode(routeRoomCode);
  const [room, setRoom] = useState(() => navigationState?.room || null);
  const [gomokuState, setGomokuState] = useState(null);
  const [error, setError] = useState('');
  const [isJoining, setIsJoining] = useState(true);
  const attemptedJoinRef = useRef(false);
  const lastJoinedRoomRef = useRef('');

  // Settings & interactive options states
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showSurrenderConfirm, setShowSurrenderConfirm] = useState(false);
  const [pendingUndoRequest, setPendingUndoRequest] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    attemptedJoinRef.current = false;
    lastJoinedRoomRef.current = '';
    setRoom(navigationState?.room || null);
    setGomokuState(null);
    setError('');
    setIsJoining(true);
  }, [navigationState, normalizedRoomCode]);

  useEffect(() => {
    if (!normalizedRoomCode || !user?.id) {
      return undefined;
    }

    function scopedRoomMatches(payload = {}) {
      const payloadCode = roomCodeFromPayload(payload);
      return !payloadCode || payloadCode === normalizedRoomCode;
    }

    function requestRoomPreview() {
      socket.emit('previewRoom', {
        roomCode: normalizedRoomCode,
        gameType: 'gomoku'
      });
    }

    function requestRoomJoin(liveRoom) {
      const existingPlayer = liveRoom?.players?.find((player) => player.userId === user.id) || null;
      const seatIndex = existingPlayer ? Number(existingPlayer.seatIndex) : firstOpenGomokuSeatIndex(liveRoom?.players || []);

      if (!existingPlayer && seatIndex < 0) {
        setIsJoining(false);
        setError('房间已满，当前版本暂不支持观战。');
        return;
      }

      if (lastJoinedRoomRef.current === normalizedRoomCode && existingPlayer) {
        setIsJoining(false);
        return;
      }

      lastJoinedRoomRef.current = normalizedRoomCode;
      socket.emit('joinRoom', {
        roomCode: normalizedRoomCode,
        gameType: 'gomoku',
        seatIndex
      });
    }

    function handleConnect() {
      requestRoomPreview();
    }

    function handleRoomPreviewed(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      const liveRoom = payload?.room || payload;
      setRoom(liveRoom);
      requestRoomJoin(liveRoom);
    }

    function handleRoomUpdated(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      const liveRoom = payload?.room || payload;
      setRoom(liveRoom);
    }

    function handleGomokuState(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      if (payload?.room) {
        setRoom(payload.room);
      }
      setGomokuState(payload?.state || null);
      setIsJoining(false);
      setError('');
      setInfoMessage('');
    }

    function handleGomokuUndoRequested(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      if (payload.requesterUserId !== user.id) {
        setPendingUndoRequest({
          requesterUserId: payload.requesterUserId,
          requesterName: payload.requesterName
        });
      }
    }

    function handleGomokuUndoDeclined(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      setInfoMessage('');
      setError(payload.message || '对方拒绝了你的悔棋请求。');
      setTimeout(() => setError(''), 4000);
    }

    function handleGomokuUndoSuccess(payload) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      setInfoMessage('');
      setError(payload.message || '悔棋成功！');
      setTimeout(() => setError(''), 4000);
    }

    function handleSocketError(payload = {}) {
      if (!scopedRoomMatches(payload)) {
        return;
      }
      clearSocketBuffer();
      setIsJoining(false);
      setError(payload.message || '五子棋房间连接失败');
    }

    function handleConnectError(errorPayload = {}) {
      clearSocketBuffer();
      setIsJoining(false);
      setError(errorPayload.message || '无法连接到五子棋房间');
    }

    socket.on('connect', handleConnect);
    socket.on('roomPreviewed', handleRoomPreviewed);
    socket.on('roomUpdated', handleRoomUpdated);
    socket.on('gomokuState', handleGomokuState);
    socket.on('gomokuUndoRequested', handleGomokuUndoRequested);
    socket.on('gomokuUndoDeclined', handleGomokuUndoDeclined);
    socket.on('gomokuUndoSuccess', handleGomokuUndoSuccess);
    socket.on('error', handleSocketError);
    socket.on('connect_error', handleConnectError);

    if (socket.connected) {
      if (!attemptedJoinRef.current) {
        attemptedJoinRef.current = true;
        requestRoomPreview();
      }
    } else {
      connectSocket();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('roomPreviewed', handleRoomPreviewed);
      socket.off('roomUpdated', handleRoomUpdated);
      socket.off('gomokuState', handleGomokuState);
      socket.off('gomokuUndoRequested', handleGomokuUndoRequested);
      socket.off('gomokuUndoDeclined', handleGomokuUndoDeclined);
      socket.off('gomokuUndoSuccess', handleGomokuUndoSuccess);
      socket.off('error', handleSocketError);
      socket.off('connect_error', handleConnectError);
    };
  }, [normalizedRoomCode, user?.id]);

  const isInLobby = !gomokuState || room?.gomokuLobby;
  const board = gomokuState?.board || createPlaceholderBoard();
  const userStone = gomokuStoneForUser(gomokuState, user?.id);
  const me = room?.players?.find((p) => p.userId === user?.id);
  const myColor = me?.color || '';

  const lobbyBlackPlayer = room?.players?.find((p) => p.color === 'B');
  const lobbyWhitePlayer = room?.players?.find((p) => p.color === 'W');

  const blackPlayerName = isInLobby
    ? (lobbyBlackPlayer?.displayName || '')
    : (participantName(room, gomokuState?.blackUserId) || '');

  const whitePlayerName = isInLobby
    ? (lobbyWhitePlayer?.displayName || '')
    : (participantName(room, gomokuState?.whiteUserId) || '');

  const userStoneDisplay = isInLobby
    ? (myColor === 'B' ? '黑子' : myColor === 'W' ? '白子' : '未入座')
    : (userStone === 'B' ? '黑子' : userStone === 'W' ? '白子' : '未入座');

  const getLobbyStatus = () => {
    if (!room || room.players.length < 2) {
      return '等待对手加入房间...';
    }
    return '准备中，请选择你的执子颜色';
  };

  const currentStatus = error
    ? error
    : infoMessage
      ? infoMessage
      : isJoining
        ? '正在进入房间...'
        : isInLobby
          ? getLobbyStatus()
          : gomokuStatusCopy({
              room,
              state: gomokuState,
              userId: user?.id
            });

  const handlePlayMove = (row, col) => {
    if (!canPlayGomokuMove({ state: gomokuState, userId: user?.id, row, col })) {
      return;
    }
    setError('');
    socket.emit('playGomokuMove', {
      roomCode: normalizedRoomCode,
      row,
      col
    });
  };

  const handleSelectColor = (color) => {
    setError('');
    socket.emit('selectGomokuColor', {
      roomCode: normalizedRoomCode,
      color
    });
  };

  const handleStartGame = () => {
    setError('');
    socket.emit('startGomokuGame', {
      roomCode: normalizedRoomCode
    });
  };

  const handlePlayAgain = () => {
    setError('');
    socket.emit('playGomokuAgain', {
      roomCode: normalizedRoomCode
    });
  };

  const handleRequestUndo = () => {
    setError('');
    setInfoMessage('已发送悔棋请求，等待对方同意...');
    socket.emit('requestGomokuUndo', { roomCode: normalizedRoomCode });
  };

  const handleRespondUndo = (agree) => {
    socket.emit('respondGomokuUndo', { roomCode: normalizedRoomCode, agree });
    setPendingUndoRequest(null);
  };

  const handleSurrender = () => {
    setError('');
    socket.emit('playGomokuSurrender', { roomCode: normalizedRoomCode });
    setShowSurrenderConfirm(false);
  };

  return (
    <main className="page-shell page-shell--themed gomoku-room-page">
      {isInLobby && (
        <header className="page-hero gomoku-hero gomoku-hero--room">
          <div>
            <h1>房间 {normalizedRoomCode}</h1>
            <p className="muted">{currentStatus}</p>
          </div>
          <button className="secondary-button secondary-button--stone" type="button" onClick={() => navigate(buildGomokuLobbyRoute())}>
            返回大厅
          </button>
        </header>
      )}

      {error ? <div className="error-message">{error}</div> : null}

      <section className="data-card gomoku-room-card">
        {isInLobby && (
          <div className="gomoku-room-card__meta">
            <div className="gomoku-player-pill">
              <span>执黑</span>
              <strong>{blackPlayerName || '等待玩家'}</strong>
            </div>
            <div className="gomoku-player-pill">
              <span>执白</span>
              <strong>{whitePlayerName || '等待玩家'}</strong>
            </div>
            <div className="gomoku-player-pill">
              <span>你的棋子</span>
              <strong>{userStoneDisplay}</strong>
            </div>
          </div>
        )}

        <div className="gomoku-board-shell">
          {!isInLobby && (
            <div className="gomoku-game-toolbar">
              <div className="gomoku-game-toolbar__status">
                <span className={`gomoku-turn-indicator gomoku-turn-indicator--${gomokuState?.currentTurnUserId === user?.id ? 'me' : 'opponent'}`} />
                <span className="gomoku-status-text">{currentStatus}</span>
              </div>
              <div className="gomoku-game-toolbar__actions">
                <button
                  type="button"
                  className="gomoku-icon-button"
                  onClick={() => setSettingsOpen(true)}
                  title="设置"
                  aria-label="设置"
                >
                  <SettingsIcon />
                </button>
                <button
                  type="button"
                  className="gomoku-toolbar-btn gomoku-toolbar-btn--undo"
                  onClick={handleRequestUndo}
                  disabled={gomokuState?.moveCount === 0 || gomokuState?.status === 'finished'}
                >
                  悔棋
                </button>
                <button
                  type="button"
                  className="gomoku-toolbar-btn gomoku-toolbar-btn--surrender"
                  onClick={() => setShowSurrenderConfirm(true)}
                  disabled={gomokuState?.status === 'finished'}
                >
                  认输
                </button>
              </div>
            </div>
          )}

          {isInLobby ? (
            <div className="gomoku-prep-shell">
              {room?.players?.length < 2 ? (
                <div className="gomoku-waiting-status">
                  <div className="gomoku-loader" />
                  <p className="gomoku-waiting-text">等待对手加入房间...</p>
                  <p className="muted text-sm">分享房间码 <strong>{normalizedRoomCode}</strong> 给好友以开始对局</p>
                </div>
              ) : (
                <div className="gomoku-color-selector">
                  <h2 className="gomoku-color-selector__title">选择你的棋子颜色</h2>
                  <div className="gomoku-color-cards">
                    <button
                      type="button"
                      className={`gomoku-color-card${myColor === 'B' ? ' gomoku-color-card--selected' : ''}`}
                      onClick={() => handleSelectColor('B')}
                    >
                      <div className="gomoku-color-stone-preview gomoku-color-stone-preview--B" />
                      <span className="gomoku-color-card__label">执黑子 (先手)</span>
                      <span className="gomoku-color-card__occupant">
                        {myColor === 'B' ? '你已选择' : lobbyBlackPlayer ? lobbyBlackPlayer.displayName : '等待选择'}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`gomoku-color-card${myColor === 'W' ? ' gomoku-color-card--selected' : ''}`}
                      onClick={() => handleSelectColor('W')}
                    >
                      <div className="gomoku-color-stone-preview gomoku-color-stone-preview--W" />
                      <span className="gomoku-color-card__label">执白子 (后手)</span>
                      <span className="gomoku-color-card__occupant">
                        {myColor === 'W' ? '你已选择' : lobbyWhitePlayer ? lobbyWhitePlayer.displayName : '等待选择'}
                      </span>
                    </button>
                  </div>

                  {room?.hostUserId === user?.id ? (
                    <button
                      type="button"
                      className="gomoku-primary-button gomoku-start-cta"
                      onClick={handleStartGame}
                    >
                      开始对局
                    </button>
                  ) : (
                    <p className="gomoku-action-text">等待房主开始对局...</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="gomoku-board" role="grid" aria-label={`五子棋房间 ${normalizedRoomCode}`}>
              {board.map((row, rowIndex) => row.map((cell, colIndex) => {
                const stoneClassName = cell ? ` gomoku-cell--${cell}` : '';
                const canPlay = canPlayGomokuMove({
                  state: gomokuState,
                  userId: user?.id,
                  row: rowIndex,
                  col: colIndex
                });

                return (
                  <button
                    key={`${rowIndex}-${colIndex}`}
                    type="button"
                    role="gridcell"
                    data-gomoku-cell
                    className={`gomoku-cell${stoneClassName}`}
                    aria-label={`第 ${rowIndex + 1} 行，第 ${colIndex + 1} 列`}
                    aria-disabled={!canPlay}
                    disabled={!canPlay}
                    onClick={() => handlePlayMove(rowIndex, colIndex)}
                  >
                    <span className="gomoku-cell__stone" aria-hidden="true">
                      {cell === 'B' ? '●' : cell === 'W' ? '○' : ''}
                    </span>
                  </button>
                );
              }))}
            </div>
          )}
        </div>

        {gomokuState?.status === 'finished' && (
          <div className="gomoku-room-card__footer">
            <button
              type="button"
              className="gomoku-primary-button"
              onClick={handlePlayAgain}
            >
              再来一局
            </button>
          </div>
        )}
      </section>

      {settingsOpen && (
        <div className="modal-backdrop" role="presentation" onClick={() => setSettingsOpen(false)}>
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="房间设置" onClick={(e) => e.stopPropagation()}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">房间设置</p>
                <h2>房间 {normalizedRoomCode}</h2>
                <p className="settings-meta">对局状态: {gomokuState?.status === 'playing' ? '进行中' : '已结束'}</p>
              </div>
              <button className="secondary-button compact-button" type="button" onClick={() => setSettingsOpen(false)}>
                关闭
              </button>
            </div>

            <div className="settings-results">
              <h3 style={{ margin: '0 0 10px 0', fontSize: '15px' }}>玩家信息</h3>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div className="gomoku-settings-player">
                  <span className="gomoku-stone-dot B" />
                  <strong>执黑:</strong>
                  <span>{blackPlayerName || '等待玩家'}</span>
                </div>
                <div className="gomoku-settings-player">
                  <span className="gomoku-stone-dot W" />
                  <strong>执白:</strong>
                  <span>{whitePlayerName || '等待玩家'}</span>
                </div>
                <div className="gomoku-settings-player">
                  <strong>你的棋子:</strong>
                  <span>{userStoneDisplay}</span>
                </div>
              </div>
            </div>

            <div className="settings-results" style={{ display: 'grid', gap: '10px' }}>
              <button
                className="secondary-button link-button"
                type="button"
                onClick={() => {
                  setSettingsOpen(false);
                  navigate('/');
                }}
              >
                返回游戏库
              </button>
              <button
                className="secondary-button link-button"
                type="button"
                onClick={() => {
                  setSettingsOpen(false);
                  navigate(buildGomokuLobbyRoute());
                }}
              >
                返回大厅
              </button>
            </div>
          </section>
        </div>
      )}

      {showSurrenderConfirm && (
        <div className="modal-backdrop" role="presentation" onClick={() => setShowSurrenderConfirm(false)}>
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="确认认输" onClick={(e) => e.stopPropagation()}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">认输二次确认</p>
                <h2>确定要认输吗？</h2>
                <p className="settings-meta">认输后将直接判定对手获胜。</p>
              </div>
            </div>
            <div className="settings-danger-zone">
              <button className="secondary-button compact-button" type="button" onClick={() => setShowSurrenderConfirm(false)}>
                取消
              </button>
              <button className="danger-button danger-button--compact" type="button" onClick={handleSurrender}>
                确认认输
              </button>
            </div>
          </section>
        </div>
      )}

      {pendingUndoRequest && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-label="悔棋请求">
            <div className="section-heading">
              <div>
                <p className="eyebrow">悔棋请求</p>
                <h2>对方请求悔棋</h2>
                <p className="settings-meta">对方 (<strong>{pendingUndoRequest.requesterName}</strong>) 请求同意悔棋。</p>
              </div>
            </div>
            <div className="settings-danger-zone">
              <button className="secondary-button compact-button" type="button" onClick={() => handleRespondUndo(false)}>
                拒绝
              </button>
              <button className="gomoku-primary-button" style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }} type="button" onClick={() => handleRespondUndo(true)}>
                同意
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default GomokuRoomPage;
